import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Link as LinkIcon, X } from "lucide-react";
import { toast } from "react-toastify";
import { cn } from "@/lib/cn";

export interface ImageViewerItem {
  /** Stable key for the image. */
  id: string;
  /** Full-resolution (or best available) source shown in the viewer. */
  src: string;
  /** Accessible description / caption for the image. */
  alt?: string;
}

interface Props {
  open: boolean;
  images: ImageViewerItem[];
  /** Index of the currently displayed image. */
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

/**
 * Full-screen image lightbox: a single image with previous/next navigation,
 * a close button, and a position counter. Supports arrow-key navigation and Esc.
 */
export function ImageViewer({ open, images, index, onIndexChange, onClose }: Props) {
  const count = images.length;
  const hasMultiple = count > 1;

  // Direction of the last navigation, used to slide the new image in from that side.
  const directionRef = useRef<"next" | "prev">("next");
  const figureRef = useRef<HTMLElement>(null);
  // Bumped on every navigation so the slide animation re-runs even when the
  // index wraps to the same value or React batches the update.
  const [animTick, setAnimTick] = useState(0);

  // Whether the currently-displayed image has finished loading. Reset whenever
  // the source changes so we show a loader (over a black frame) until it's ready.
  const [loaded, setLoaded] = useState(false);
  const safeIndex = count ? Math.min(Math.max(index, 0), count - 1) : 0;
  const currentSrc = images[safeIndex]?.src;
  useEffect(() => {
    setLoaded(false);
  }, [currentSrc]);

  const handleShare = useCallback(async () => {
    const src = images[safeIndex]?.src;
    if (!src) return;
    // Resolve to an absolute URL so the copied link works outside the app.
    const url = new URL(src, window.location.origin).href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Image link copied to clipboard.");
    } catch {
      toast.error("Could not copy the image link.");
    }
  }, [images, safeIndex]);

  const goPrev = useCallback(() => {
    if (count === 0) return;
    directionRef.current = "prev";
    setAnimTick((t) => t + 1);
    onIndexChange((index - 1 + count) % count);
  }, [count, index, onIndexChange]);

  const goNext = useCallback(() => {
    if (count === 0) return;
    directionRef.current = "next";
    setAnimTick((t) => t + 1);
    onIndexChange((index + 1) % count);
  }, [count, index, onIndexChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Stop the event from reaching listeners on underlying overlays (e.g. a
        // Drawer behind the viewer) so Escape only closes the viewer itself.
        e.stopImmediatePropagation();
        onClose();
      } else if (hasMultiple && e.key === "ArrowLeft") goPrev();
      else if (hasMultiple && e.key === "ArrowRight") goNext();
    };
    // Capture phase so this runs before bubble-phase window listeners
    // registered by components rendered behind the viewer.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose, goPrev, goNext, hasMultiple]);

  // Slide the frame in from the navigated direction. Driven imperatively (Web
  // Animations API) so it replays reliably on every move and is visible even
  // before the new image finishes loading.
  useEffect(() => {
    if (!open || animTick === 0) return;
    const el = figureRef.current;
    if (!el) return;
    const fromX = directionRef.current === "next" ? 64 : -64;
    el.animate(
      [
        { transform: `translateX(${fromX}px)`, opacity: 0 },
        { transform: "translateX(0)", opacity: 1 },
      ],
      { duration: 280, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
    );
  }, [animTick, open]);

  if (!open || count === 0) return null;

  const current = images[safeIndex];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={onClose}
    >
      <div className="absolute top-5 right-6 flex items-center gap-4">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            void handleShare();
          }}
          aria-label="Copy link to this image"
          title="Copy link to this image"
          className="text-white/90 hover:text-white transition-colors"
        >
          <LinkIcon size={24} strokeWidth={2.5} />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close image viewer"
          className="text-white/90 hover:text-white transition-colors"
        >
          <X size={32} strokeWidth={2.5} />
        </button>
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        {hasMultiple && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous image"
            className="shrink-0 text-white/90 hover:text-white transition-transform hover:-translate-x-0.5 active:scale-90"
          >
            <ChevronLeft size={48} strokeWidth={3} />
          </button>
        )}

        <figure
          ref={figureRef}
          // Fixed 4:3 frame so every image occupies an identically-shaped box.
          // Width is the largest 4:3 box that fits within 90vw / 85vh (4/3 * 85vh = 113.33vh).
          className="relative aspect-[4/3] w-[min(90vw,113.33vh)] bg-black"
          onClick={(e) => e.stopPropagation()}
        >
          {!loaded && (
            <span
              aria-hidden
              className="absolute inset-0 flex items-center justify-center"
            >
              <span className="h-9 w-9 rounded-full border-2 border-white/25 border-t-white animate-spin" />
            </span>
          )}
          <img
            // Force a remount per source so onLoad fires reliably and a cached
            // image doesn't briefly show the previous picture.
            key={current.id}
            src={current.src}
            alt={current.alt ?? `Image ${index + 1} of ${count}`}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
            className={cn(
              "h-full w-full object-contain select-none transition-opacity duration-200",
              loaded ? "opacity-100" : "opacity-0",
            )}
            draggable={false}
          />
        </figure>

        {hasMultiple && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next image"
            className="shrink-0 text-white/90 hover:text-white transition-transform hover:translate-x-0.5 active:scale-90"
          >
            <ChevronRight size={48} strokeWidth={3} />
          </button>
        )}
      </div>

      {hasMultiple && (
        <div className={cn("absolute bottom-6 right-8 text-sm font-medium text-white/80")}>
          {index + 1} of {count}
        </div>
      )}
    </div>,
    document.body,
  );
}
